import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Flex, Modal, Select, Space, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { ErrorCode } from '@app/models/error';
import { hasErrorCode } from '@app/services/apiErrors';
import { getCadesError } from '@app/services/cades';
import { formatDate } from '@app/services/date';
import { useComputerCheck } from '@app/services/hooks/useComputerCheck';
import { RAW_INTERPOLATION } from '@app/services/i18n';
import { CadesChecks } from '@components/CadesChecks';
import { EpdError } from '@components/EpdError';

import { useEpdSigning } from './hooks/useEpdSigning';
import { useSigningContext } from './hooks/useSigningContext';
import { ISigningResult, ISigningTarget } from './interfaces';

interface IProps {
  targets: ISigningTarget[];
  onClose: () => void;
}

export const EpdSigningModal: React.FC<IProps> = ({ targets, onClose }) => {
  const epdT = useTranslation(NSKeys.epd);
  const cadesT = useTranslation(NSKeys.cades);
  const commonT = useTranslation(NSKeys.common);
  const check = useComputerCheck();
  const context = useSigningContext();
  const signing = useEpdSigning();
  const client = useQueryClient();
  const [thumbprint, setThumbprint] = React.useState<string>();
  const [groundUid, setGroundUid] = React.useState<string>();
  const [results, setResults] = React.useState<ISigningResult[]>([]);
  const certificate = check.result?.certificates.find((item) => item.thumbprint === thumbprint);
  const grounds = context.data?.certificates.find((item) => item.thumbprint === thumbprint)?.grounds || [];
  const ground = grounds.find((item) => item.poa_uid === groundUid);
  const eligible =
    check.result?.certificates.filter(
      (item) =>
        item.valid &&
        item.hasPrivateKey &&
        context.data?.certificates.some((entry) => entry.thumbprint === item.thumbprint && entry.grounds.length)
    ) || [];
  const retryTargets = targets.filter((target) =>
    results.some(
      (item) =>
        item.uid === target.uid &&
        hasErrorCode(item.error, [ErrorCode.EPD_DATA_CHANGED, ErrorCode.EPD_SIGNING_UNAVAILABLE])
    )
  );
  const started = results.length > 0 || signing.isError;
  const locked = signing.isLoading || (started && !retryTargets.length);

  React.useEffect(() => {
    if (check.result?.ready) context.mutate(check.result.certificates);
  }, [check.result]);

  const sign = async () => {
    if (!certificate || !ground || !check.result || signing.isLoading) return;
    const pending = started ? retryTargets : targets;
    let chosen = ground;
    if (started) {
      await client.invalidateQueries(['epd']);
      const fresh = await context.mutateAsync(check.result.certificates).catch(() => undefined);
      const found = fresh?.certificates
        .find((item) => item.thumbprint === certificate.thumbprint)
        ?.grounds.find((item) => item.poa_uid === ground.poa_uid);
      if (!found) {
        setGroundUid(undefined);
        return;
      }
      chosen = found;
    }
    setResults((values) => values.filter((item) => !pending.some((target) => target.uid === item.uid)));
    signing.mutate({
      targets: pending,
      certificate,
      ground: chosen,
      onProgress: (result) => setResults((values) => [...values, result]),
    });
  };

  const close = () => {
    signing.cancel();
    onClose();
  };

  return (
    <Modal
      open
      title={epdT.t('sign')}
      width={720}
      destroyOnHidden
      onCancel={close}
      footer={
        <Space>
          <Button onClick={close}>{commonT.t('buttonClose')}</Button>
          {(!started || !!retryTargets.length) && (
            <Button
              type="primary"
              loading={signing.isLoading}
              disabled={!certificate || !ground || !check.result?.ready || context.isLoading || !targets.length}
              onClick={sign}
            >
              {started ? epdT.t('signAgain') : epdT.t('sign')}
            </Button>
          )}
        </Space>
      }
    >
      <Flex vertical gap={16}>
        <CadesChecks
          result={check.result}
          running={check.running}
          withDiagnosticLink={!check.result?.ready || (!!context.data && !eligible.length)}
        />
        {context.isLoading && <Spin />}
        {context.isError && <EpdError error={context.error} />}
        {context.data && !eligible.length && <Alert type="warning" showIcon title={epdT.t('noGrounds')} />}
        {eligible.length > 0 && (
          <>
            <Typography.Text strong>{context.data?.legal_entity.name}</Typography.Text>
            <Select
              aria-label={cadesT.t('certificate')}
              placeholder={cadesT.t('certificate')}
              value={thumbprint}
              disabled={locked}
              onChange={(value) => {
                setThumbprint(value);
                setGroundUid(undefined);
              }}
              options={eligible.map((item) => ({
                value: item.thumbprint,
                label: `${[item.identity.lastName, item.identity.firstName, item.identity.middleName]
                  .filter(Boolean)
                  .join(' ')} · ${item.serial}`,
              }))}
            />
            <Select
              aria-label={epdT.t('ground')}
              placeholder={epdT.t('ground')}
              value={groundUid}
              disabled={!certificate || locked}
              onChange={setGroundUid}
              options={grounds.map((item) => ({
                value: item.poa_uid,
                label: item.is_head
                  ? epdT.t('head')
                  : epdT.t('poaGround', {
                      number: item.number,
                      date: formatDate(item.valid_until),
                      ...RAW_INTERPOLATION,
                    }),
              }))}
            />
          </>
        )}
        {targets.map((target) => {
          const result = results.find((item) => item.uid === target.uid);
          return (
            <Flex key={target.uid} vertical gap={8}>
              <Typography.Text strong>
                {target.number} · {epdT.t(`titles.${target.title}` as const)}
              </Typography.Text>
              {result?.error ? (
                <EpdError error={result.error} />
              ) : result?.pluginError ? (
                <Alert
                  type="error"
                  showIcon
                  title={cadesT.t(result.pluginError.includes('0xC2110F00') ? 'hints.trustedSite' : 'hints.license')}
                  description={result.pluginError}
                />
              ) : result ? (
                <Alert
                  type={result.status === 'sent' ? 'success' : 'info'}
                  showIcon
                  title={epdT.t(result.status === 'sent' ? 'signed' : 'sending')}
                />
              ) : (
                signing.isLoading && <Spin size="small" />
              )}
            </Flex>
          );
        })}
        {signing.isError && (
          <Alert type="error" showIcon title={cadesT.t('hints.license')} description={getCadesError(signing.error)} />
        )}
      </Flex>
    </Modal>
  );
};
